import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import cx from 'classnames';
import DateRangeIcon from '@material-ui/icons/DateRange';
import { ButtonBaseProps, Drawer, Fab, Tooltip, Typography } from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';
import CloseIcon from '@material-ui/icons/Close';
import { makeStyles } from '@material-ui/core/styles';

import Zone from 'components/shared/Zone';
import Stack from 'components/shared/Stack';
import { StyledIconButton } from 'components/shared/MuiExtensions';

import { RootState } from 'state/store';
import Script from './Script';
import CopyLeadDetails from './CopyLeadDetails';
import { IElement, PanelModel, QuestionRadiogroupModel, ReactSurveyModel } from 'survey-react';
import { AppCopyText, LoadingStates, ScriptLeadStatusActions } from '../../typings';
import { apiInvalidPhoneLead } from '../../state/lead.state';
import Loader from 'components/Loader';

interface PanelProps {
  className?: string;
}

interface CalendarProps extends ButtonBaseProps {
  appointmentLink?: string;
  alert?: boolean;
}

const useStyles = makeStyles((theme) => ({
  fab: {
    position: 'absolute',
    bottom: theme.spacing(2),
    right: theme.spacing(2),
    color: theme.palette.primary.main,
  },
  paper: {
    width: '50vh',
    maxWidth: '500px',
  },
  animatedItem: {
    animation: '$wobble 1s ease 0s infinite normal forwards',
  },
  '@keyframes wobble': {
    '0%': {
      transform: 'translateX(0%)',
      'transform-origin': '50% 50%',
    },
    '15%': {
      transform: 'translateX(-30px) rotate(-6deg)',
    },
    '30%': {
      transform: 'translateX(15px) rotate(6deg)',
    },
    '45%': {
      transform: 'translateX(-15px) rotate(-3.6deg)',
    },
    '60%': {
      transform: 'translateX(9px) rotate(2.4deg)',
    },
    '75%': {
      transform: 'translateX(-6px) rotate(-1.2deg)',
    },
    '100%': {
      transform: 'translateX(0 %)',
      'transform-origin': '50% 50%',
    },
  },
}));

export const getAutoSelectQuestion = (
  elements: Array<IElement>
): QuestionRadiogroupModel | null => {
  const autoSelectActions = [
    ScriptLeadStatusActions.QUALIFY,
    ScriptLeadStatusActions.UNQUALIFY,
    ScriptLeadStatusActions.GOOD_EMAIL_BAD_PHONE,
  ];

  const question = elements.find((element) => {
    return (
      element instanceof QuestionRadiogroupModel &&
      element.choices.length === 1 &&
      autoSelectActions.includes(element.choices[0].itemValue)
    );
  });

  if (question) {
    return question as QuestionRadiogroupModel;
  }

  return null;
};

const Calendar = ({ appointmentLink = '', alert = false, ...buttonBaseProps }: CalendarProps) => {
  const [openDrawer, setOpenDrawer] = useState(false);
  const styles = useStyles();

  const toggleDrawerOpen = (drawerOpen: boolean) => () => {
    setOpenDrawer(drawerOpen);
  };

  const handleButtonClick = () => {
    if (!appointmentLink) return;
    setOpenDrawer(true);
  };

  return (
    <div>
      <Tooltip title="Book Appointment" placement="left" arrow>
        <StyledIconButton
          className={alert ? styles.animatedItem : ''}
          onClick={handleButtonClick}
          {...buttonBaseProps}
        >
          <DateRangeIcon color="secondary" fontSize="medium" />
        </StyledIconButton>
      </Tooltip>
      <Drawer
        variant="persistent"
        anchor="right"
        open={openDrawer}
        onClose={toggleDrawerOpen(false)}
        ModalProps={{
          hideBackdrop: true,
        }}
        classes={{ paper: styles.paper }}
      >
        <iframe
          title="Book an Appointment"
          src={appointmentLink}
          height="100%"
          frameBorder="0"
          sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
        />
        {openDrawer && (
          <Fab
            size="small"
            className={styles.fab}
            aria-label="close"
            onClick={toggleDrawerOpen(false)}
          >
            <CloseIcon />
          </Fab>
        )}
      </Drawer>
    </div>
  );
};

const MemoScript = React.memo(Script, (prevProps, nextProps) => {
  return prevProps.data.leadId === nextProps.data.leadId && prevProps.style === nextProps.style;
});

const RightPanel = ({ className }: PanelProps) => {
  const dispatch = useDispatch();
  const darkMode = useSelector((state: RootState) => state.app.darkMode);
  const [calendarAppointment, setCalendarAppointment] = useState({
    url: '',
    alert: false,
  });
  const extraNoteForRep = useSelector(
    (state: RootState) => state.lead.entity?.agent?.extraNoteForRep
  );
  const unqualifyReasons = useSelector((state: RootState) => state.lead.unqualifyReasons);
  const isLoading = useSelector(
    (state: RootState) => state.script.loadingState === LoadingStates.LOADING
  );
  const { scriptEntity } = useSelector(
    (state: RootState) => ({
      scriptEntity: state.script.entity,
    }),
    (a: any, b: any) => a.scriptEntity?.leadId === b.scriptEntity?.leadId
  );

  const handleScriptStepChange = (
    step: string,
    isCurrentStep: boolean,
    survey: ReactSurveyModel,
    options: { panel?: PanelModel }
  ) => {
    const apptStepCalendarUrl = scriptEntity?.scriptStepsByName?.[step]?.appointmentUrl ?? '';

    if (apptStepCalendarUrl) {
      setCalendarAppointment({ url: apptStepCalendarUrl, alert: !!isCurrentStep });
    }

    if (options.panel) {
      const autoSelectQuestion = getAutoSelectQuestion(options.panel.elements);

      if (autoSelectQuestion) {
        const selectedValue = autoSelectQuestion.choices[0].itemValue;

        survey.setValue(autoSelectQuestion.name, selectedValue);
        autoSelectQuestion.showClearButton = true;

        if (selectedValue === ScriptLeadStatusActions.GOOD_EMAIL_BAD_PHONE) {
          dispatch(apiInvalidPhoneLead({ reason: AppCopyText.BAD_PHONE_REASON }));
        }
      }
    }
  };

  const handleCalendarMouseOver = () => {
    setCalendarAppointment({ ...calendarAppointment, alert: false });
  };

  return (
    <Zone
      className={cx('grid-panel', className)}
      heading={
        <div className="horizontal-space">
          {isLoading && <Loader />}
          <span>script</span>
          {!!calendarAppointment.url && (
            <Calendar
              appointmentLink={calendarAppointment.url}
              alert={calendarAppointment.alert}
              onMouseOver={handleCalendarMouseOver}
              onFocus={handleCalendarMouseOver}
            />
          )}
        </div>
      }
    >
      <Stack className="stack-container">
        {extraNoteForRep && (
          <Typography className="extra-note" variant="caption" color="textPrimary">
            <InfoIcon fontSize="inherit" /> {extraNoteForRep}
          </Typography>
        )}
        {scriptEntity && (
          <div className="script-wrapper">
            <div className="script">
              <MemoScript
                data={scriptEntity}
                unqualifyReasons={unqualifyReasons}
                onStepRender={handleScriptStepChange}
                style={darkMode ? 'dark' : 'light'}
              />
            </div>
          </div>
        )}
        <div>
          <CopyLeadDetails />
        </div>
      </Stack>
    </Zone>
  );
};

export default styled(RightPanel)`
  & {
    position: relative;
  }

  .stack-container {
    height: 100%;
    justify-content: space-between;
  }

  .script-wrapper {
    position: relative;
    height: 100%;
  }

  .script {
    height: 100%;
    position: absolute;
    overflow: hidden;
    overflow-y: scroll;
    font-size: var(--s-1);
    width: 100%;
  }

  .extra-note {
    font-weight: 900;
    font-style: italic;
    font-size: var(--s-1);
  }
`;
